import { useId } from 'react';
import { fieldChecked, useModalOpen } from '../../app/components/polaris';
import { ProductVariantFitmentScope, VariantFitmentReview } from '../../app/models/Product';

export function VariantTargetPicker({ variants, currentVariantId, selected, disabled, onToggle, onSelect }: {
    open: boolean; variants: ProductVariantFitmentScope[]; currentVariantId: number | null; selected: number[]; disabled: boolean;
    onToggle: () => void; onClose: () => void; onSelect: (ids: number[]) => void;
}) {
    const popoverId = `pf-variant-picker-${useId().replace(/:/g, '')}`;
    const otherIds = variants.filter(v => v.id !== currentVariantId).map(v => v.id);
    return <>
        <s-button variant="tertiary" accessibilityLabel="Choose variants" disabled={disabled} commandFor={popoverId} onClick={onToggle}>Choose…</s-button>
        <s-popover id={popoverId} maxBlockSize="360px">
            <s-box padding="base">
                <s-stack gap="small-200" accessibilityLabel="Choose variants">
                    <s-checkbox label="Select all" checked={selected.length === otherIds.length}
                        indeterminate={selected.length > 0 && selected.length < otherIds.length}
                        disabled={disabled} onChange={event => onSelect(fieldChecked(event) ? otherIds : [])} />
                    {selected.length > 100 && <s-text tone="critical">Choose up to 100 other variants.</s-text>}
                    <s-divider />
                    {variants.map(variant => <s-checkbox key={variant.id}
                        label={`${[variant.sku, variant.title].filter(Boolean).join(' — ') || `Variant ${variant.id}`}${variant.id === currentVariantId ? ' (current)' : ''}`}
                        checked={variant.id === currentVariantId || selected.includes(variant.id)}
                        disabled={disabled || variant.id === currentVariantId}
                        onChange={event => onSelect(fieldChecked(event) ? [...selected, variant.id] : selected.filter(id => id !== variant.id))} />)}
                </s-stack>
            </s-box>
        </s-popover>
    </>;
}

export const variantHasChanges = (variant: VariantFitmentReview['variants'][number]) =>
    variant.added + variant.removed + variant.notesUpdated > 0;

// Older servers combine missing matches and harmless no-ops. Keep their review
// visible until the server supplies separate missing-match counts.
export const variantMissingMatches = (variant: VariantFitmentReview['variants'][number]) =>
    variant.missingMatches ?? variant.skipped;

export function VariantReviewDialog({ review, currentVariantId, busy, retry, error, onClose, onExclude, onSave }: {
    review: VariantFitmentReview | null; currentVariantId: number | null; busy: boolean; retry: boolean; error: string;
    onClose: () => void; onExclude: (ids: number[]) => void; onSave: () => void;
}) {
    const conflicts = review?.variants.filter(variant => variant.conflicts.length > 0) ?? [];
    const missing = review?.variants.filter(variant => variantMissingMatches(variant) > 0) ?? [];
    const affectedCount = review?.variants.filter(variantHasChanges).length ?? 0;
    const canExclude = conflicts.length > 0 && conflicts.every(variant => variant.variantId !== currentVariantId);
    const partialMatches = missing.some(variantHasChanges);
    const unknownSkips = missing.some(variant => variant.missingMatches === undefined);
    const modalRef = useModalOpen(!!review, () => { if (!busy && !retry) onClose(); });
    return <s-modal ref={modalRef} id="pf-variant-review-modal" heading={conflicts.length ? 'Fitment conflicts' : 'Some fitments don’t match'}>
        {review && <s-stack gap="base">
            {error && <s-banner tone="critical">{error}</s-banner>}
            {conflicts.length > 0 ? <>
                {!canExclude && <s-paragraph>Resolve the current variant’s conflict before saving.</s-paragraph>}
                {conflicts.map(variant => <s-stack key={variant.variantId} gap="small-300">
                    <s-heading>{variant.label}</s-heading>
                    {Array.from(new Set(variant.conflicts)).map(conflict => <s-text key={conflict} tone="critical">{conflict}</s-text>)}
                </s-stack>)}
            </> : <s-paragraph>
                Updates {affectedCount} {affectedCount === 1 ? 'variant' : 'variants'}.{' '}
                {unknownSkips ? 'Some changes are already applied or have no matching fitments.' : partialMatches
                    ? `${missing.length} ${missing.length === 1 ? 'variant has' : 'variants have'} unmatched fitments. Only matching edits will apply.`
                    : `${missing.length} ${missing.length === 1 ? 'has' : 'have'} no matching fitments.`}
            </s-paragraph>}
        </s-stack>}
        {conflicts.length > 0
            ? canExclude && <s-button slot="primary-action" variant="primary" loading={busy}
                onClick={() => onExclude(conflicts.map(v => v.variantId))}>{`Exclude ${conflicts.length} and save`}</s-button>
            : <s-button slot="primary-action" variant="primary" loading={busy}
                disabled={!review?.canSave || affectedCount === 0} onClick={onSave}>{retry ? 'Retry save' : `Save to ${affectedCount}`}</s-button>}
        <s-button slot="secondary-actions" disabled={busy || retry} onClick={onClose}>Cancel</s-button>
    </s-modal>;
}
